import React from 'react';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {
	getLabelValueAutoListOptionLabel,
	isLabelValueAutoListOptionSelected,
} from '@netx/core-next/utils/autoList';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {faUsers} from '@netx/core-icons';
import {
	AutoListField,
	AutoListFieldOption,
	type AutoListCommonExtendProps,
} from '@netx/core-components/AutoListField';
import {getGroupAutoList} from '../../api';
import {GroupLabel} from '../../locales';
import {type IGroupAutoListItem} from '../../schema';
export {type IGroupAutoListItem} from '../../schema';

export interface GroupAutoListProps<
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	IGroupAutoListItem,
	Multiple,
	DisableClearable,
	false
> {}

/**
 * Group auto list field
 */
export const GroupAutoList = <
	Multiple extends boolean = true,
	DisableClearable extends boolean = false,
>({
	filterOptions,
	getOptionLabel = getLabelValueAutoListOptionLabel,
	getOptionSelected = isLabelValueAutoListOptionSelected,
	onClose,
	onInputChange,
	onOpen,
	...props
}: GroupAutoListProps<Multiple, DisableClearable>) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			filterOptions,
			queryFn: getGroupAutoList,
		},
	);

	const placeholder =
		props.multiple !== true ? GroupLabel.SELECT_GROUP : undefined;

	return (
		<AutoListField<IGroupAutoListItem, Multiple, DisableClearable>
			inputValue={queryStr}
			label={GroupLabel.ADD_GROUP}
			loadingOptions={loadingOptions}
			name="group-auto-list"
			placeholder={placeholder}
			filterOptions={filterOptions}
			isOptionEqualToValue={getOptionSelected}
			renderOption={(props, option, state) => (
				<AutoListFieldOption
					value={getOptionLabel(option)}
					avatar
					icon={option.value === -1 ? faUsers : undefined}
					{...props}
				/>
			)}
			{...props}
			options={query.data ?? []}
			getOptionLabel={getOptionLabel}
			onClose={splitEventHandler(onClose, () => setIsOpen(false))}
			onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
			onInputChange={splitEventHandler(onInputChange, (event, value, reason) =>
				setQueryStr(value),
			)}
		/>
	);
};

export default GroupAutoList;
